import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, CheckCircle2, Shield, Scale, Lock,
    Building2, Phone, User, Briefcase, Gauge, DollarSign, Sparkles,
    Zap, XCircle, ChevronRight,
} from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_SIZE_OPTS = [
    { value: "1-10",       label: "1–10 employees" },
    { value: "11-50",      label: "11–50 employees" },
    { value: "51-200",     label: "51–200 employees" },
    { value: "201-1000",   label: "201–1,000 employees" },
    { value: "1000+",      label: "1,000+ employees" },
];

const MATTER_VOLUME_OPTS = [
    { value: "<10/mo",      label: "Fewer than 10 matters / month" },
    { value: "10-50/mo",    label: "10–50 matters / month" },
    { value: "50-200/mo",   label: "50–200 matters / month" },
    { value: "200+/mo",     label: "200+ matters / month" },
];

const LEGAL_SPEND_OPTS = [
    { value: "<$50k/yr",      label: "Under $50k / year" },
    { value: "$50k-250k/yr",  label: "$50k – $250k / year" },
    { value: "$250k-1M/yr",   label: "$250k – $1M / year" },
    { value: "$1M+/yr",       label: "Over $1M / year" },
];

const PAIN_OPTS = [
    { value: "outside_counsel_costs",   label: "Outside counsel costs are out of control" },
    { value: "contract_review_backlog", label: "Contract-review backlog is slowing the business" },
    { value: "compliance_burden",       label: "Compliance burden (SOC 2, GDPR, HIPAA, etc.)" },
    { value: "litigation_load",         label: "Litigation load — too many matters, not enough attorneys" },
    { value: "matter_management",       label: "Matter management + coordination across a large team" },
    { value: "other",                   label: "Something else — I'll share on the call" },
];

const INTEGRATION_OPTS = [
    "iManage", "NetDocuments", "Clio", "Litify", "Filevine", "DocuSign", "PandaDoc",
    "Ironclad", "SpotDraft", "Contract Logix", "SharePoint", "Google Drive",
    "Microsoft 365", "Salesforce", "HubSpot", "Slack", "Microsoft Teams",
    "Zoom", "PACER / CM/ECF", "Westlaw", "LexisNexis", "Bloomberg Law", "Other",
];

const STEP_LABELS = ["Basics", "Volume & spend", "Priorities", "Timing", "Confirmation"];

export default function LegalEnterpriseInquiry() {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        full_name: "",
        work_email: "",
        company_name: "",
        phone: "",
        company_size: "",
        role_or_title: "",
        matter_volume: "",
        current_legal_spend: "",
        primary_pain: "",
        integration_needs: [],
        preferred_call_time: "",
        additional_context: "",
        website: "", // honeypot
    });
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleIntegration = (name) =>
        set(
            "integration_needs",
            form.integration_needs.includes(name)
                ? form.integration_needs.filter((n) => n !== name)
                : [...form.integration_needs, name]
        );

    /* --------------------------- Step validation --------------------------- */
    const stepValid = useMemo(() => {
        if (step === 1) {
            return (
                form.full_name.trim().length >= 2 &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email) &&
                form.company_name.trim().length >= 2 &&
                !!form.company_size &&
                form.role_or_title.trim().length >= 2
            );
        }
        if (step === 2) return !!form.matter_volume && !!form.current_legal_spend;
        if (step === 3) return !!form.primary_pain;
        if (step === 4) return true; // timing is optional
        return true;
    }, [step, form]);

    const next = () => {
        if (!stepValid) {
            toast.error("Please fill in the required fields to continue.");
            return;
        }
        setStep((s) => Math.min(s + 1, 5));
    };
    const prev = () => setStep((s) => Math.max(s - 1, 1));

    const submit = async () => {
        setSubmitting(true);
        setErrorMsg("");
        try {
            await axios.post(`${API_BASE}/legal/enterprise-inquiry`, {
                ...form,
                source_page: typeof window !== "undefined" ? window.location.pathname : null,
            });
            setStep(5);
        } catch (e) {
            const msg =
                e.response?.data?.detail ||
                "Could not submit your inquiry. Please try again or email hello@nexus-one.com.";
            setErrorMsg(typeof msg === "string" ? msg : "Something went wrong.");
            toast.error(typeof msg === "string" ? msg : "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fafaf7]" data-testid="legal-enterprise-inquiry-page">
            {/* Header */}
            <header className="border-b border-[#e8e8e3] bg-white">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-[#16a34a] grid place-items-center">
                            <Scale className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                        <span className="text-[15px] font-semibold text-gray-900">Nexus One · Legal</span>
                    </Link>
                    <Link
                        to="/pricing"
                        className="text-[13px] text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
                        data-testid="inquiry-back-to-pricing"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to pricing
                    </Link>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 py-10">
                {/* Hero */}
                {step < 5 && (
                    <div className="mb-8">
                        <div className="wfos-label text-gray-500">Legal Enterprise · Book a call</div>
                        <h1 className="mt-2 text-[32px] font-semibold tracking-tight text-gray-900 leading-tight max-w-2xl">
                            Bring the entire Legal AI Department into your firm.
                        </h1>
                        <p className="mt-3 text-[15px] text-gray-600 max-w-2xl">
                            Tell us about your team and we'll structure a Legal Enterprise plan around your matter
                            volume, integration stack, and supervision requirements. Dr. Askew or our Legal
                            Enterprise lead responds within one business day.
                        </p>
                        <TrustBar />
                    </div>
                )}

                {/* Progress */}
                {step < 5 && <StepProgress step={step} />}

                {/* Card */}
                <div className="mt-6 wfos-card overflow-hidden bg-white">
                    {/* Honeypot — visually hidden but present in DOM. Bots fill it. */}
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        value={form.website}
                        onChange={(e) => set("website", e.target.value)}
                        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                        data-testid="inquiry-honeypot"
                    />

                    <div className="p-6 md:p-8">
                        {step === 1 && <StepBasics form={form} set={set} />}
                        {step === 2 && <StepVolume form={form} set={set} />}
                        {step === 3 && <StepPriorities form={form} set={set} toggleIntegration={toggleIntegration} />}
                        {step === 4 && <StepTiming form={form} set={set} />}
                        {step === 5 && <StepConfirmation form={form} />}
                    </div>

                    {/* Footer nav */}
                    {step < 5 && (
                        <div className="border-t border-[#e8e8e3] bg-[#fafaf7] px-6 md:px-8 py-4 flex items-center justify-between">
                            {step > 1 ? (
                                <button
                                    onClick={prev}
                                    className="inline-flex items-center gap-1 text-[13px] text-gray-600 hover:text-gray-900"
                                    data-testid="inquiry-step-back"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back
                                </button>
                            ) : (
                                <div />
                            )}
                            {step < 4 ? (
                                <button
                                    onClick={next}
                                    disabled={!stepValid}
                                    data-testid="inquiry-step-next"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRight className="w-4 h-4" strokeWidth={2} />
                                </button>
                            ) : (
                                <button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="inquiry-submit"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#111827] px-5 h-10 text-sm font-medium text-white hover:bg-black disabled:opacity-60"
                                >
                                    <Sparkles className="w-4 h-4" strokeWidth={2} />
                                    {submitting ? "Submitting…" : "Book Enterprise Call"}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {errorMsg && (
                    <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[13px] text-rose-800" data-testid="inquiry-error">
                        <XCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2} />
                        {errorMsg}
                    </div>
                )}

                {step < 5 && (
                    <div className="mt-8 text-center text-[11.5px] text-gray-400">
                        <Lock className="w-3 h-3 inline -mt-0.5 mr-1" strokeWidth={2} />
                        Your information stays with our Legal Enterprise team. We never share prospect
                        data. See our <Link to="/legal" className="underline hover:text-gray-600">privacy notice</Link>.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ============================== Sub-components ============================== */
function TrustBar() {
    return (
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-gray-500" data-testid="inquiry-trust-bar">
            <span className="inline-flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />
                Attorney-supervised outputs
            </span>
            <span className="inline-flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />
                18-agent Legal AI Department
            </span>
            <span className="inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />
                SOC 2 in flight · Ethical walls · Audit log per matter
            </span>
        </div>
    );
}

function StepProgress({ step }) {
    return (
        <div className="flex items-center gap-2" data-testid="inquiry-progress">
            {STEP_LABELS.slice(0, 4).map((label, i) => {
                const n = i + 1;
                const done = step > n;
                const active = step === n;
                return (
                    <React.Fragment key={label}>
                        <div className="flex items-center gap-2">
                            <span
                                className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold ${
                                    done ? "bg-[#16a34a] text-white"
                                    : active ? "bg-[#111827] text-white"
                                    : "bg-white border border-[#e8e8e3] text-gray-400"
                                }`}
                            >
                                {done ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> : n}
                            </span>
                            <span className={`text-[12px] ${active ? "text-gray-900 font-medium" : "text-gray-500"}`}>{label}</span>
                        </div>
                        {i < 3 && <ChevronRight className="w-3 h-3 text-gray-300" strokeWidth={2} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function Field({ label, required, children, hint }) {
    return (
        <div>
            <label className="block text-[12.5px] font-medium text-gray-800 mb-1.5">
                {label} {required && <span className="text-[#dc2626]">*</span>}
            </label>
            {children}
            {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
        </div>
    );
}

function Input({ testid, icon: Icon, ...props }) {
    return (
        <div className="relative">
            {Icon && (
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
            )}
            <input
                data-testid={testid}
                {...props}
                className={`w-full rounded-lg border border-[#e8e8e3] bg-white h-11 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] focus:border-[#16a34a] ${Icon ? "pl-9" : "pl-3"} pr-3`}
            />
        </div>
    );
}

function Select({ testid, icon: Icon, children, ...props }) {
    return (
        <div className="relative">
            {Icon && (
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
            )}
            <select
                data-testid={testid}
                {...props}
                className={`w-full rounded-lg border border-[#e8e8e3] bg-white h-11 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] focus:border-[#16a34a] ${Icon ? "pl-9" : "pl-3"} pr-3 appearance-none`}
            >
                {children}
            </select>
            <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" strokeWidth={2} />
        </div>
    );
}

function StepBasics({ form, set }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name" required>
                <Input icon={User} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Chen" testid="inquiry-full-name" autoFocus />
            </Field>
            <Field label="Work email" required>
                <Input value={form.work_email} onChange={(e) => set("work_email", e.target.value)} type="email" placeholder="jane@firm.com" testid="inquiry-work-email" />
            </Field>
            <Field label="Company / firm" required>
                <Input icon={Building2} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Chen & Partners LLP" testid="inquiry-company" />
            </Field>
            <Field label="Phone">
                <Input icon={Phone} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 (555) 010-0100" testid="inquiry-phone" />
            </Field>
            <Field label="Company size" required>
                <Select icon={Briefcase} value={form.company_size} onChange={(e) => set("company_size", e.target.value)} testid="inquiry-company-size">
                    <option value="">Select…</option>
                    {COMPANY_SIZE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
            </Field>
            <Field label="Your role / title" required>
                <Input value={form.role_or_title} onChange={(e) => set("role_or_title", e.target.value)} placeholder="Chief Legal Officer" testid="inquiry-role" />
            </Field>
        </div>
    );
}

function StepVolume({ form, set }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="How many active matters do you handle per month?" required>
                <Select icon={Gauge} value={form.matter_volume} onChange={(e) => set("matter_volume", e.target.value)} testid="inquiry-matter-volume">
                    <option value="">Select…</option>
                    {MATTER_VOLUME_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
            </Field>
            <Field label="Current annual legal spend (fees + tools + headcount)" required>
                <Select icon={DollarSign} value={form.current_legal_spend} onChange={(e) => set("current_legal_spend", e.target.value)} testid="inquiry-legal-spend">
                    <option value="">Select…</option>
                    {LEGAL_SPEND_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
            </Field>
            <div className="md:col-span-2 rounded-lg border border-[#e8e8e3] bg-[#fafaf7] p-4 text-[12.5px] text-gray-700 leading-relaxed">
                <b>Why we ask.</b> These two numbers alone tell us whether Legal Enterprise is the right
                shape for you (or whether Legal Professional at $3,999/mo covers it). We'll never quote
                a price before we understand the fit.
            </div>
        </div>
    );
}

function StepPriorities({ form, set, toggleIntegration }) {
    return (
        <div className="space-y-6">
            <Field label="What's the single biggest legal pain right now?" required>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                    {PAIN_OPTS.map((o) => {
                        const active = form.primary_pain === o.value;
                        return (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => set("primary_pain", o.value)}
                                data-testid={`inquiry-pain-${o.value}`}
                                className={`text-left rounded-lg border p-3 text-[13.5px] transition-colors ${
                                    active ? "border-[#16a34a] bg-[#f0fdf4] text-gray-900" : "border-[#e8e8e3] bg-white text-gray-700 hover:border-[#c7c7bd]"
                                }`}
                            >
                                <span className={`inline-flex items-center gap-2 ${active ? "font-medium" : ""}`}>
                                    <span className={`w-3.5 h-3.5 rounded-full border ${active ? "border-[#16a34a] bg-[#16a34a]" : "border-gray-300"}`} />
                                    {o.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </Field>

            <Field label="Which of these do you need us to integrate with?" hint="Multi-select — pick any that apply. We'll cover the top requests in your bundle.">
                <div className="flex flex-wrap gap-1.5" data-testid="inquiry-integrations">
                    {INTEGRATION_OPTS.map((name) => {
                        const active = form.integration_needs.includes(name);
                        return (
                            <button
                                key={name}
                                type="button"
                                onClick={() => toggleIntegration(name)}
                                data-testid={`inquiry-integration-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className={`px-2.5 py-1 rounded-full text-[11.5px] font-medium border transition-colors ${
                                    active ? "border-[#16a34a] bg-[#16a34a] text-white" : "border-[#e8e8e3] bg-white text-gray-700 hover:border-[#c7c7bd]"
                                }`}
                            >
                                {name}
                            </button>
                        );
                    })}
                </div>
            </Field>
        </div>
    );
}

function StepTiming({ form, set }) {
    return (
        <div className="space-y-5">
            <Field label="When's a good time to reach you?" hint="Optional. We usually respond within a business day — this just helps us hit your calendar right.">
                <Input value={form.preferred_call_time} onChange={(e) => set("preferred_call_time", e.target.value)} placeholder="Tue–Thu after 2pm ET" testid="inquiry-preferred-time" />
            </Field>
            <Field label="Anything else we should know?" hint="Ideal team size, upcoming compliance deadline, current tools you're replacing, etc.">
                <textarea
                    value={form.additional_context}
                    onChange={(e) => set("additional_context", e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="e.g., We're a 12-attorney corporate boutique. We're evaluating Ironclad + Clio replacements. Contract-review turnaround is our bottleneck."
                    data-testid="inquiry-additional-context"
                    className="w-full rounded-lg border border-[#e8e8e3] bg-white p-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] focus:border-[#16a34a] resize-y"
                />
            </Field>
            <div className="rounded-lg border border-[#e8e8e3] bg-[#fafaf7] p-4 text-[12.5px] text-gray-700 leading-relaxed">
                By submitting, you agree we may contact you about Legal Enterprise. We don't share prospect
                data with third parties. You can request deletion at any time.
            </div>
        </div>
    );
}

function StepConfirmation({ form }) {
    return (
        <div className="py-6 text-center max-w-lg mx-auto" data-testid="inquiry-confirmation">
            <div className="w-14 h-14 rounded-full bg-[#dcfce7] grid place-items-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[#16a34a]" strokeWidth={1.75} />
            </div>
            <h2 className="mt-5 text-[24px] font-semibold text-gray-900">
                We got it, {form.full_name.split(" ")[0] || "there"}.
            </h2>
            <p className="mt-3 text-[15px] text-gray-600 leading-relaxed">
                Dr. Askew or our Legal Enterprise team will reach out within <b>one business day</b>. We'll
                come with a scoped plan for a firm your size ({form.company_size}) doing{" "}
                {form.matter_volume} of matter volume.
            </p>
            <div className="mt-6 rounded-lg border border-[#e8e8e3] bg-[#fafaf7] p-4 text-left">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-2">
                    In the meantime
                </div>
                <ul className="space-y-2 text-[13.5px] text-gray-800">
                    <li className="flex items-start gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-[#16a34a] mt-0.5" strokeWidth={2} />
                        <Link to="/app/legal" className="hover:text-[#16a34a]">
                            Explore the Legal AI Department — 18 supervised legal agents
                        </Link>
                    </li>
                    <li className="flex items-start gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-[#16a34a] mt-0.5" strokeWidth={2} />
                        <Link to="/pricing" className="hover:text-[#16a34a]">
                            See Legal Starter and Legal Professional — for smaller teams starting today
                        </Link>
                    </li>
                    <li className="flex items-start gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-[#16a34a] mt-0.5" strokeWidth={2} />
                        <Link to="/sectors/legal-services" className="hover:text-[#16a34a]">
                            Read how firms like yours deploy Nexus One
                        </Link>
                    </li>
                </ul>
            </div>
            <Link
                to="/app/legal"
                className="mt-6 inline-flex items-center gap-1 text-[13px] font-medium text-[#16a34a] hover:text-[#15803d]"
                data-testid="inquiry-confirmation-explore-legal"
            >
                Explore the Legal AI Department <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
        </div>
    );
}
